/* eslint-disable max-lines */
// lib
import { Set } from 'immutable'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import React, { Component } from 'react'
import { elementDimensions, DOMElementRect, DOMchildOf, stopEvent } from '@aytm/lib/common/func'

// components
import ManagedRenderComponent from '@aytm/lib/common/components/ManagedRenderComponent'
import Portal from '@aytm/lib/rfd-ui/components/basic/portal'

// styles
import '@aytm/lib/rfd-ui/styles/basic/popup.scss'

// constants
const Z_INDEX_POPUP_PORTAL = 9020

// TRIGGERS:
//  - click
//  - hover
export const DEFAULT_TRIGGER = 'click'

// DIRECTIONS:
//  - right
//  - left
//  - top
//  - bottom
//  - topright
//  - topleft
//  - bottomright
//  - bottomleft
//  - righttop
//  - rightbottom
//  - lefttop
//  - leftbottom
//  - rightdown
//  - rightup
//  - leftdown
//  - leftup
export const DEFAULT_DIRECTION = 'rightdown'

export const ARROW_CLASS_BY_DIRECTION = {
  right: 'rfd-ui-popup-arrow-left',
  left: 'rfd-ui-popup-arrow-right',
  top: 'rfd-ui-popup-arrow-bottom',
  bottom: 'rfd-ui-popup-arrow-top',
  topright: 'rfd-ui-popup-arrow-bottomright',
  topleft: 'rfd-ui-popup-arrow-bottomleft',
  bottomright: 'rfd-ui-popup-arrow-topright',
  bottomleft: 'rfd-ui-popup-arrow-topleft',
  righttop: 'rfd-ui-popup-arrow-lefttop',
  rightbottom: 'rfd-ui-popup-arrow-leftbottom',
  lefttop: 'rfd-ui-popup-arrow-righttop',
  leftbottom: 'rfd-ui-popup-arrow-rightbottom',
  rightdown: 'rfd-ui-popup-arrow-left',
  rightup: 'rfd-ui-popup-arrow-left',
  leftdown: 'rfd-ui-popup-arrow-right',
  leftup: 'rfd-ui-popup-arrow-right'
}

export const BORDER_BY_DIRECTION = {
  right: 'right',
  left: 'left',
  top: 'top',
  bottom: 'bottom',
  topright: 'top',
  topleft: 'top',
  bottomright: 'bottom',
  bottomleft: 'bottom',
  righttop: 'right',
  rightbottom: 'right',
  lefttop: 'left',
  leftbottom: 'left',
  rightdown: 'right',
  rightup: 'right',
  leftdown: 'left',
  leftup: 'left'
}

export const HEADER_POSITION_BY_DIRECTION = {
  right: 'top',
  left: 'top',
  top: 'bottom',
  bottom: 'top',
  topright: 'bottom',
  topleft: 'bottom',
  bottomright: 'top',
  bottomleft: 'top',
  righttop: 'top',
  rightbottom: 'bottom',
  lefttop: 'top',
  leftbottom: 'bottom',
  rightdown: 'top',
  rightup: 'bottom',
  leftdown: 'top',
  leftup: 'bottom'
}

const popups = {
  opened: Set()
}

/*
 * <Popup>
 */

export default class Popup extends ManagedRenderComponent {
  static propTypes = {
    show: PropTypes.bool,
    trigger: PropTypes.string,
    direction: PropTypes.string,
    portal: PropTypes.bool,
    hideOnLeave: PropTypes.bool,
    leaveTimeout: PropTypes.number,
    hideOtherPopupsOnShow: PropTypes.bool,
    dontHideOnOtherPopupShown: PropTypes.bool,
    renderRef: PropTypes.any,
    className: PropTypes.string,
    targetClassName: PropTypes.string,
    accentColor: PropTypes.string,

    onShow: PropTypes.func,
    onHide: PropTypes.func,

    targetStyle: PropTypes.object,

    width: PropTypes.string,
    arrowOffset: PropTypes.number,
    popupOffset: PropTypes.number,
    content: PropTypes.func,
    ContentComponent: PropTypes.func,
    contentStyle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    contentTheme: PropTypes.string,

    header: PropTypes.func,
    headerPosition: PropTypes.string,
    HeaderComponent: PropTypes.func,
    headerStyle: PropTypes.object,
    headerTheme: PropTypes.string,
    headerCloseButton: PropTypes.bool
  }
  static updateOnProps(nextProps, prevProps) {
    return (
      nextProps.renderRef != prevProps.renderRef ||
      nextProps.show != prevProps.show ||
      nextProps.trigger != prevProps.trigger ||
      nextProps.portal != prevProps.portal ||
      nextProps.direction != prevProps.direction ||
      nextProps.width != prevProps.width ||
      nextProps.hideOnLeave != prevProps.hideOnLeave ||
      nextProps.leaveTimeout != prevProps.leaveTimeout ||
      nextProps.hideOtherPopupsOnShow != prevProps.hideOtherPopupsOnShow ||
      nextProps.dontHideOnOtherPopupShown != prevProps.dontHideOnOtherPopupShown ||
      nextProps.targetStyle != prevProps.targetStyle ||
      nextProps.arrowOffset != prevProps.arrowOffset ||
      nextProps.popupOffset != prevProps.popupOffset ||
      nextProps.ContentComponent != prevProps.ContentComponent ||
      nextProps.contentStyle != prevProps.contentStyle ||
      nextProps.contentTheme != prevProps.contentTheme ||
      nextProps.headerPosition != prevProps.headerPosition ||
      nextProps.HeaderComponent != prevProps.HeaderComponent ||
      nextProps.headerStyle != prevProps.headerStyle ||
      nextProps.headerTheme != prevProps.headerTheme ||
      nextProps.headerCloseButton != prevProps.headerCloseButton ||
      nextProps.content != prevProps.content ||
      nextProps.className != prevProps.className ||
      nextProps.targetClassName != prevProps.targetClassName
    )
  }

  static contextTypes = {
    popupTrigger: PropTypes.instanceOf(ManagedRenderComponent)
  }

  static childContextTypes = {
    popup: PropTypes.instanceOf(ManagedRenderComponent).isRequired,
    popupDirection: PropTypes.string.isRequired
  }
  getChildContext() {
    return {
      popup: this,
      popupDirection: this.props.direction || DEFAULT_DIRECTION
    }
  }

  constructor(props) {
    super(props)
    this.show = this.show.bind(this)
    this.hide = this.hide.bind(this)
    this.freeze = this.freeze.bind(this)
    this.unfreeze = this.unfreeze.bind(this)
    this.onClickTrigger = this.onClickTrigger.bind(this)
    this.onClickTarget = this.onClickTarget.bind(this)
    this.onEnterTrigger = this.onEnterTrigger.bind(this)
    this.onEnterTarget = this.onEnterTarget.bind(this)
    this.onLeaveTrigger = this.onLeaveTrigger.bind(this)
    this.onLeaveTarget = this.onLeaveTarget.bind(this)
    this.onEnterPopup = this.onEnterPopup.bind(this)
    this.onLeavePopup = this.onLeavePopup.bind(this)
    this.getDimensions = this.getDimensions.bind(this)
    this.renderContentInline = this.renderContentInline.bind(this)
    this.updatePortalTargetRect = this.updatePortalTargetRect.bind(this)
    this.onWindowResize = this.onWindowResize.bind(this)
    this.watchPortalTargetRect = this.watchPortalTargetRect.bind(this)
    this.renderContentInPortal = this.renderContentInPortal.bind(this)
    this.state = {
      show: Boolean(props.show),
      portalTargetRect: null
    }
    this.hideOnGlobalClick = null
    this.hideOnLeave = false
    this.hideOnLeaveTimer = null
    this.enteredPopup = false
    this.frozen = false

    if (props.show) {
      if (props.hideOtherPopupsOnShow) popups.opened.forEach(popup => (this != popup ? popup.hide() : null))
      if (!props.dontHideOnOtherPopupShown) popups.opened = popups.opened.add(this)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { show, portal } = this.props
    if (show != prevProps.show) {
      this.setState({ show: Boolean(show) })
    }
    if (portal && this.state.show) {
      this.updatePortalTargetRect()
    }
    if (this.state.show != prevState.show || portal != prevProps.portal) {
      this.watchPortalTargetRect(this.state.show)
    }
    if (this.state.show != prevState.show) {
      const { onShow, onHide } = this.props
      if (this.state.show && 'function' == typeof onShow) {
        onShow()
      } else if (!this.state.show && 'function' == typeof onHide) {
        onHide()
      }
    }
    if (this.state.arrowOffset != prevState.arrowOffset || this.state.popupOffset != prevProps.popupOffset) {
      portal && this.updatePortalTargetRect()
    }
  }

  componentDidMount() {
    if (super.componentDidMount) {
      super.componentDidMount()
    }
    const { portal } = this.props
    const { show } = this.state
    const { popupTrigger } = this.context
    this.enteredPopup = false
    if (popupTrigger) popupTrigger.registerPopup(this)
    if (show && portal) {
      this.updatePortalTargetRect()
      this.watchPortalTargetRect(true)
    }
  }

  componentWillUnmount() {
    if (super.componentWillUnmount) {
      super.componentWillUnmount()
    }
    this.watchPortalTargetRect(false)
    this.hide()
  }

  show(callback) {
    if (this.frozen) return
    const { hideOtherPopupsOnShow = false, dontHideOnOtherPopupShown = false } = this.props
    if (hideOtherPopupsOnShow) popups.opened.forEach(popup => (this != popup ? popup.hide() : null))
    if (!dontHideOnOtherPopupShown) popups.opened = popups.opened.add(this)
    this.setState({ show: true }, callback)
  }

  hide(callback) {
    if (this.frozen) return
    popups.opened = popups.opened.remove(this)
    this.enteredPopup = false
    this.setState({ show: false, portalTargetRect: null }, callback)
    if (this.hideOnGlobalClick) {
      document.removeEventListener('click', this.hideOnGlobalClick, true)
      this.hideOnGlobalClick = null
    }
    this.hideOnLeave = false
    if (this.hideOnLeaveTimer) clearTimeout(this.hideOnLeaveTimer)
  }

  freeze() {
    this.frozen = true
  }

  unfreeze() {
    this.frozen = false
  }

  onClickTrigger(event, triggeredBy = this) {
    const popupElement = this.refPopup && this.refPopup.refPopup ? this.refPopup.refPopup : null
    if (popupElement && DOMchildOf(event.target, popupElement)) {
      return false
    }
    const { show } = this.state
    if (show) this.hide()
    else {
      this.show(() => {
        document.addEventListener(
          'click',
          (this.hideOnGlobalClick = event => {
            const triggerElement = triggeredBy.refPopupTrigger || triggeredBy.refPopupTarget
            const popupElement = this.refPopup && this.refPopup.refPopup ? this.refPopup.refPopup : null
            if (
              (!triggerElement || !DOMchildOf(event.target, triggerElement)) &&
              (!popupElement || !DOMchildOf(event.target, popupElement))
            ) {
              this.onClickTrigger(event, triggeredBy)
            }
          }),
          true
        )
      })
    }
    return true
  }

  onClickTarget(event) {
    this.onClickTrigger(event, this)
  }

  onEnterTrigger(/*e, triggeredBy = this*/) {
    if (this.hideOnLeaveTimer) clearTimeout(this.hideOnLeaveTimer)
    this.hideOnLeave = null == this.props.hideOnLeave ? true : this.props.hideOnLeave
    this.show()
  }

  onEnterTarget(event) {
    this.onEnterTrigger(event, this)
  }

  onLeaveTrigger(/*e, triggeredBy = this*/) {
    const { hideOnLeave, leaveTimeout } = this.props
    if (this.hideOnLeave && false !== hideOnLeave) {
      if (this.hideOnLeaveTimer) clearTimeout(this.hideOnLeaveTimer)
      this.hideOnLeaveTimer = setTimeout(() => {
        this.hideOnLeaveTimer = null
        if (!this.enteredPopup) {
          this.hide()
        }
      }, leaveTimeout || 200)
    }
  }

  onLeaveTarget(event) {
    this.onLeaveTrigger(event, this)
  }

  onEnterPopup() {
    this.enteredPopup = true
  }

  onLeavePopup(event) {
    this.enteredPopup = false
    this.onLeaveTrigger(event, this.refPopup)
  }

  getDimensions({ fast = false } = {}) {
    return this.refPopupTarget
      ? elementDimensions(this.refPopupTarget, fast)
      : { width: null, widthUnits: '', height: null, heightUnits: '' }
  }

  renderContentInline(content) {
    return content
  }

  updatePortalTargetRect(callback) {
    if (this.refPopupTarget) {
      const { portalTargetRect: curPortalTargetRect } = this.state
      const portalTargetRect = DOMElementRect(this.refPopupTarget)
      if (
        null == curPortalTargetRect ||
        curPortalTargetRect.top != portalTargetRect.top ||
        curPortalTargetRect.bottom != portalTargetRect.bottom ||
        curPortalTargetRect.left != portalTargetRect.left ||
        curPortalTargetRect.right != portalTargetRect.right ||
        curPortalTargetRect.width != portalTargetRect.width ||
        curPortalTargetRect.height != portalTargetRect.height
      ) {
        this.setState({ portalTargetRect }, callback)
      } else {
        if ('function' == typeof callback) {
          callback()
        }
      }
    } else {
      this.setState({ portalTargetRect: null }, callback)
    }
  }

  onWindowResize() {
    this.updatePortalTargetRect()
  }

  watchPortalTargetRect(watch = true) {
    if (watch && !this.portalTargetRectWatcherAdded) {
      window.addEventListener('resize', this.onWindowResize)
      this.portalTargetRectWatcherAdded = true
    } else if (this.portalTargetRectWatcherAdded) {
      window.removeEventListener('resize', this.onWindowResize)
      this.portalTargetRectWatcherAdded = false
    }
  }

  renderContentInPortal(content) {
    const { direction = DEFAULT_DIRECTION } = this.props
    const { portalTargetRect } = this.state

    if (null == portalTargetRect) {
      return null
    }

    const borderByDirection = BORDER_BY_DIRECTION[direction]

    return (
      <Portal
        nodeStyle={{
          position: 'absolute',
          zIndex: Z_INDEX_POPUP_PORTAL,
          left: 0,
          top: 0,
          width: 0,
          height: 0,
          overflow: 'visible'
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 'right' == borderByDirection ? portalTargetRect.right : portalTargetRect.left,
            right: 'left' == borderByDirection ? portalTargetRect.left : portalTargetRect.right,
            top: 'bottom' == borderByDirection ? portalTargetRect.bottom : portalTargetRect.top,
            bottom: 'top' == borderByDirection ? portalTargetRect.top : portalTargetRect.bottom,
            width: 'left' == borderByDirection || 'right' == borderByDirection ? 0 : portalTargetRect.width,
            height: 'top' == borderByDirection || 'bottom' == borderByDirection ? 0 : portalTargetRect.height
          }}
        >
          {content}
        </div>
      </Portal>
    )
  }

  render() {
    const {
      trigger,
      direction = DEFAULT_DIRECTION,
      portal = false,

      targetStyle,

      width: contentWidth,
      arrowOffset = 0,
      popupOffset = 0,
      content,
      ContentComponent = PopupContent,
      accentColor,
      contentStyle,
      contentTheme,
      className,
      targetClassName,

      header,
      headerPosition,
      HeaderComponent,
      headerStyle,
      headerTheme,
      headerCloseButton,

      renderRef,
      children
    } = this.props
    const { show } = this.state
    return (
      <span
        className={`rfd-ui-popup-target${show ? ' rfd-ui-popup-target-active' : ''}${
          targetClassName ? ' ' + targetClassName : ''
        }`}
        style={targetStyle}
        ref={this.saveRef('PopupTarget')}
      >
        <span
          className="rfd-ui-popup-target-content"
          onClick={'click' == trigger ? this.onClickTarget : null}
          onMouseEnter={'hover' == trigger ? this.onEnterTarget : null}
          onMouseLeave={'hover' == trigger ? this.onLeaveTarget : null}
        >
          {children}
        </span>
        {show && 'function' == typeof content
          ? (portal ? this.renderContentInPortal : this.renderContentInline)(
              <ContentComponent
                direction={direction}
                accentColor={accentColor}
                width={contentWidth}
                arrowOffset={arrowOffset}
                popupOffset={popupOffset}
                className={className}
                content={content}
                contentStyle={contentStyle}
                contentTheme={contentTheme}
                header={header}
                headerPosition={headerPosition}
                HeaderComponent={HeaderComponent}
                headerStyle={headerStyle}
                headerTheme={headerTheme}
                headerCloseButton={headerCloseButton}
                ref={this.saveRef('Popup')}
                onMouseEnter={this.onEnterPopup}
                onMouseLeave={this.onLeavePopup}
                hidePopup={this.hide}
                renderRef={renderRef}
              />
            )
          : null}
      </span>
    )
  }
}

/*
 * <PopupContent>
 */

export class PopupContent extends ManagedRenderComponent {
  static propTypes = {
    direction: PropTypes.string,

    width: PropTypes.string,
    arrowOffset: PropTypes.number,
    popupOffset: PropTypes.number,
    content: PropTypes.func.isRequired,
    contentStyle: PropTypes.object,
    contentTheme: PropTypes.string,

    className: PropTypes.string,

    header: PropTypes.func,
    headerPosition: PropTypes.string,
    HeaderComponent: PropTypes.func,
    headerStyle: PropTypes.object,
    headerTheme: PropTypes.string,
    headerCloseButton: PropTypes.bool,

    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    hidePopup: PropTypes.func,

    renderRef: PropTypes.any
  }
  static updateOnProps(nextProps, prevProps) {
    return (
      nextProps.renderRef != prevProps.renderRef ||
      nextProps.direction != prevProps.direction ||
      nextProps.width != prevProps.width ||
      nextProps.arrowOffset != prevProps.arrowOffset ||
      nextProps.popupOffset != prevProps.popupOffset ||
      nextProps.className != prevProps.className ||
      nextProps.content != prevProps.content ||
      nextProps.contentStyle != prevProps.contentStyle ||
      nextProps.contentTheme != prevProps.contentTheme ||
      nextProps.headerPosition != prevProps.headerPosition ||
      nextProps.HeaderComponent != prevProps.HeaderComponent ||
      nextProps.headerStyle != prevProps.headerStyle ||
      nextProps.headerTheme != prevProps.headerTheme ||
      nextProps.headerCloseButton != prevProps.headerCloseButton
    )
  }

  static childContextTypes = {
    popupContent: PropTypes.instanceOf(ManagedRenderComponent).isRequired
  }
  getChildContext() {
    return {
      popupContent: this
    }
  }

  static repositionStyles = {
    right: d => {
      return {
        popup: {
          top: '50%',
          right: `-${d.popup.width + d.arrow.width}${d.popup.widthUnits}`,
          margin: `-${d.popup.height / 2 + d.arrow.offset}${d.popup.heightUnits} 0 0 0`
        },
        arrow: {
          top: `${d.popup.height / 2 - d.arrow.height / 2 + d.arrow.offset}${d.popup.heightUnits}`,
          left: `-${d.arrow.width}${d.arrow.widthUnits}`
        }
      }
    },
    righttop: d => {
      return {
        popup: {
          top: `${d.popup.offset}${d.popup.heightUnits}`,
          right: `-${d.popup.width + d.arrow.width}${d.popup.widthUnits}`,
          margin: 0
        },
        arrow: {
          top: `${d.arrow.offset}${d.arrow.heightUnits}`,
          left: `-${d.arrow.width}${d.arrow.widthUnits}`
        }
      }
    },
    rightbottom: d => {
      return {
        popup: {
          bottom: `${d.popup.offset}${d.popup.heightUnits}`,
          right: `-${d.popup.width + d.arrow.width}${d.popup.widthUnits}`,
          margin: 0
        },
        arrow: {
          bottom: `${d.arrow.offset}${d.arrow.heightUnits}`,
          left: `-${d.arrow.width}${d.arrow.widthUnits}`
        }
      }
    },
    left: d => {
      return {
        popup: {
          top: '50%',
          left: `-${d.popup.width + d.arrow.width}${d.popup.widthUnits}`,
          margin: `-${d.popup.height / 2 + d.arrow.offset}${d.popup.heightUnits} 0 0 0`
        },
        arrow: {
          top: `${d.popup.height / 2 - d.arrow.height / 2 + d.arrow.offset}${d.popup.heightUnits}`,
          right: `-${d.arrow.width}${d.arrow.widthUnits}`
        }
      }
    },
    lefttop: d => {
      return {
        popup: {
          top: `${d.popup.offset}${d.popup.heightUnits}`,
          left: `-${d.popup.width + d.arrow.width}${d.popup.widthUnits}`,
          margin: 0
        },
        arrow: {
          top: `${d.arrow.offset}${d.arrow.heightUnits}`,
          right: `-${d.arrow.width}${d.arrow.widthUnits}`
        }
      }
    },
    leftbottom: d => {
      return {
        popup: {
          bottom: `${d.popup.offset}${d.popup.heightUnits}`,
          left: `-${d.popup.width + d.arrow.width}${d.popup.widthUnits}`,
          margin: 0
        },
        arrow: {
          bottom: `${d.arrow.offset}${d.arrow.heightUnits}`,
          right: `-${d.arrow.width}${d.arrow.widthUnits}`
        }
      }
    },
    top: d => {
      return {
        popup: {
          left: '50%',
          margin: `0 0 0 -${d.popup.width / 2 + d.arrow.offset}${d.popup.widthUnits}`,
          top: `-${d.popup.height + d.arrow.height}${d.popup.heightUnits}`
        },
        arrow: {
          left: `${d.popup.width / 2 - d.arrow.width / 2 + d.arrow.offset}${d.popup.widthUnits}`,
          bottom: `-${d.arrow.height}${d.arrow.heightUnits}`
        }
      }
    },
    topright: d => {
      return {
        popup: {
          right: `${d.popup.offset}${d.popup.widthUnits}`,
          top: `-${d.popup.height + d.arrow.height}${d.popup.heightUnits}`,
          margin: 0
        },
        arrow: {
          right: `${d.arrow.offset}${d.arrow.widthUnits}`,
          bottom: `-${d.arrow.height}${d.arrow.heightUnits}`
        }
      }
    },
    topleft: d => {
      return {
        popup: {
          left: `${d.popup.offset}${d.popup.widthUnits}`,
          top: `-${d.popup.height + d.arrow.height}${d.popup.heightUnits}`,
          margin: 0
        },
        arrow: {
          left: `${d.arrow.offset}${d.arrow.widthUnits}`,
          bottom: `-${d.arrow.height}${d.arrow.heightUnits}`
        }
      }
    },
    bottom: d => {
      return {
        popup: {
          left: '50%',
          margin: `0 0 0 -${d.popup.width / 2 + d.arrow.offset}${d.popup.widthUnits}`,
          bottom: `-${d.popup.height + d.arrow.height}${d.popup.heightUnits}`
        },
        arrow: {
          left: `${d.popup.width / 2 - d.arrow.width / 2 + d.arrow.offset}${d.popup.widthUnits}`,
          top: `-${d.arrow.height}${d.arrow.heightUnits}`
        }
      }
    },
    bottomright: d => {
      return {
        popup: {
          right: `${d.popup.offset}${d.popup.widthUnits}`,
          bottom: `-${d.popup.height + d.arrow.height}${d.popup.heightUnits}`,
          margin: 0
        },
        arrow: {
          right: `${d.arrow.offset}${d.arrow.widthUnits}`,
          top: `-${d.arrow.height}${d.arrow.heightUnits}`
        }
      }
    },
    bottomleft: d => {
      return {
        popup: {
          left: `${d.popup.offset}${d.popup.widthUnits}`,
          bottom: `-${d.popup.height + d.arrow.height}${d.popup.heightUnits}`,
          margin: 0
        },
        arrow: {
          left: `${d.arrow.offset}${d.arrow.widthUnits}`,
          top: `-${d.arrow.height}${d.arrow.heightUnits}`
        }
      }
    },
    rightdown: d => {
      return {
        popup: {
          top: '50%',
          right: `-${d.popup.width + d.arrow.width}${d.popup.widthUnits}`,
          margin: `-${d.arrow.height / 2}${d.popup.heightUnits} 0 0 0`
        },
        arrow: {
          top: `${d.arrow.offset}${d.arrow.heightUnits}`,
          left: `-${d.arrow.width}${d.arrow.widthUnits}`
        }
      }
    },
    rightup: d => {
      return {
        popup: {
          top: '50%',
          right: `-${d.popup.width + d.arrow.width}${d.popup.widthUnits}`,
          margin: `-${d.popup.height - d.arrow.height / 2}${d.popup.heightUnits} 0 0 0`
        },
        arrow: {
          top: `${d.popup.height - d.arrow.height - d.arrow.offset}${d.arrow.heightUnits}`,
          left: `-${d.arrow.width}${d.arrow.widthUnits}`
        }
      }
    },
    leftdown: d => {
      return {
        popup: {
          top: '50%',
          left: `-${d.popup.width + d.arrow.width}${d.popup.widthUnits}`,
          margin: `-${d.arrow.height / 2}${d.popup.heightUnits} 0 0 0`
        },
        arrow: {
          top: `${d.arrow.offset}${d.arrow.heightUnits}`,
          right: `-${d.arrow.width}${d.arrow.widthUnits}`
        }
      }
    },
    leftup: d => {
      return {
        popup: {
          top: '50%',
          left: `-${d.popup.width + d.arrow.width}${d.popup.widthUnits}`,
          margin: `-${d.popup.height - d.arrow.height / 2}${d.popup.heightUnits} 0 0 0`
        },
        arrow: {
          top: `${d.popup.height - d.arrow.height - d.arrow.offset}${d.arrow.heightUnits}`,
          right: `-${d.arrow.width}${d.arrow.widthUnits}`
        }
      }
    }
  }

  constructor(props) {
    super(props)
    this.getDimensions = this.getDimensions.bind(this)
    this.reposition = this.reposition.bind(this)
    this.saveRefPopup = this.saveRefPopup.bind(this)
    this.renderHeader = this.renderHeader.bind(this)
    this.state = {
      positioned: false,
      popupPosition: null,
      arrowPosition: null
    }
  }

  getDimensions({ popup = true, arrow = true, fast = false } = {}) {
    const positions = {}

    if (popup && this.refPopup) {
      const { popupOffset = 0 } = this.props
      positions.popup = elementDimensions(this.refPopup, fast)
      positions.popup.offset = popupOffset
    }

    if (arrow && this.refPopupArrow) {
      const { arrowOffset = 0 } = this.props
      positions.arrow = elementDimensions(this.refPopupArrow, fast)
      positions.arrow.offset = arrowOffset
    }

    return positions
  }

  reposition({ direction = this.props.direction, silent = false } = {}) {
    const apply = () => {
      if (this.refPopup && this.refPopupArrow) {
        const positions =
          (PopupContent.repositionStyles[direction] || PopupContent.repositionStyles[DEFAULT_DIRECTION])(
            this.getDimensions()
          ) || {}
        this.setState({
          positioned: true,
          popupPosition: positions.popup,
          arrowPosition: positions.arrow
        })
      }
    }
    if (silent) {
      apply()
    } else {
      this.setState({ positioned: false }, apply)
    }
  }

  saveRefPopup(element) {
    if (this.refPopup != element) {
      if (this.resizeObserver) {
        if (this.refPopup) {
          this.resizeObserver.unobserve(this.refPopup)
        }
        if (element) {
          this.resizeObserver.observe(element)
        }
      }
      this.refPopup = element
    }
  }

  componentDidMount() {
    this.reposition()
    if ('function' == typeof ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.reposition({ silent: true })
      })
      if (this.refPopup) {
        this.resizeObserver.observe(this.refPopup)
      }
    }
  }

  componentWillUnmount() {
    if (super.componentWillUnmount) {
      super.componentWillUnmount()
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.direction != this.props.direction ||
      prevProps.width != this.props.width ||
      prevProps.contentStyle != this.props.contentStyle ||
      prevProps.contentTheme != this.props.contentTheme ||
      prevProps.headerPosition != this.props.headerPosition ||
      prevProps.HeaderComponent != this.props.HeaderComponent ||
      prevProps.headerStyle != this.props.headerStyle ||
      prevProps.headerTheme != this.props.headerTheme ||
      prevProps.arrowOffset != this.props.arrowOffset ||
      prevProps.popupOffset != this.props.popupOffset
    ) {
      this.reposition()
    }
  }

  renderHeader() {
    const {
      header,
      headerPosition,
      HeaderComponent = PopupContentHeader,
      headerStyle,
      headerTheme,
      headerCloseButton,
      renderRef
    } = this.props
    return 'function' == typeof header ? (
      <HeaderComponent
        header={header}
        headerPosition={headerPosition}
        HeaderComponent={HeaderComponent}
        headerStyle={headerStyle}
        headerTheme={headerTheme}
        headerCloseButton={headerCloseButton}
        renderRef={renderRef}
      />
    ) : null
  }

  render() {
    const {
      direction,
      width: contentWidth,
      className,
      content,
      contentStyle,
      contentTheme,
      accentColor,
      header,
      onMouseEnter,
      onMouseLeave,
      hidePopup
    } = this.props

    const headerPosition =
      'top' == this.props.headerPosition || 'bottom' == this.props.headerPosition
        ? this.props.headerPosition
        : HEADER_POSITION_BY_DIRECTION[direction] || HEADER_POSITION_BY_DIRECTION[DEFAULT_DIRECTION]
    const { positioned, popupPosition, arrowPosition } = this.state
    return (
      <div
        className={classNames('rfd-ui-popup', className, {
          'rfd-ui-popup-header-top': 'function' == typeof header && 'bottom' != headerPosition,
          'rfd-ui-popup-header-bottom': 'function' == typeof header && 'bottom' == headerPosition,
          'rfd-ui-popup-hidden': !positioned
        })}
        ref={this.saveRefPopup}
        style={null == contentWidth ? popupPosition : Object.assign({}, popupPosition || {}, { width: contentWidth })}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div
          className={`rfd-ui-popup-arrow ${
            ARROW_CLASS_BY_DIRECTION[direction] || ARROW_CLASS_BY_DIRECTION[DEFAULT_DIRECTION]
          }`}
          style={Object.assign(accentColor ? { borderLeftColor: accentColor } : {}, arrowPosition || {})}
          ref={this.saveRef('PopupArrow')}
        />
        {'bottom' != headerPosition && this.renderHeader()}
        <div
          className={classNames('rfd-ui-popup-content', { [`rfd-ui-popup-content-${contentTheme}`]: contentTheme })}
          style={contentStyle}
        >
          {'function' == typeof content ? content({ hidePopup }) : null}
        </div>
        {'bottom' == headerPosition && this.renderHeader()}
      </div>
    )
  }
}

/*
 * <PopupContentHeader>
 */

export class PopupContentHeader extends ManagedRenderComponent {
  static propTypes = {
    header: PropTypes.func,
    headerPosition: PropTypes.string,
    HeaderComponent: PropTypes.func,
    headerStyle: PropTypes.object,
    headerTheme: PropTypes.string,
    headerCloseButton: PropTypes.bool,
    renderRef: PropTypes.any
  }
  static updateOnProps(nextProps, prevProps) {
    return (
      nextProps.renderRef != prevProps.renderRef ||
      nextProps.headerPosition != prevProps.headerPosition ||
      nextProps.HeaderComponent != prevProps.HeaderComponent ||
      nextProps.headerStyle != prevProps.headerStyle ||
      nextProps.headerTheme != prevProps.headerTheme ||
      nextProps.headerCloseButton != prevProps.headerCloseButton
    )
  }

  render() {
    const { header, headerStyle, headerTheme, headerCloseButton = false } = this.props
    return (
      <div
        className={classNames('rfd-ui-popup-header', { [`rfd-ui-popup-header-${headerTheme}`]: headerTheme })}
        style={headerStyle}
      >
        {headerCloseButton && (
          <PopupCloseButton>
            <i className="icon-aytmfd-close" />
          </PopupCloseButton>
        )}
        {header()}
      </div>
    )
  }
}

/*
 * <PopupCloseButton>
 */

export class PopupCloseButton extends ManagedRenderComponent {
  static propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    onClose: PropTypes.func
  }
  static updateOnProps(nextProps, prevProps) {
    return nextProps.className != prevProps.className || nextProps.style != prevProps.style
  }

  static contextTypes = {
    popup: PropTypes.instanceOf(ManagedRenderComponent)
  }

  constructor(...args) {
    super(...args)
    this.closePopup = this.closePopup.bind(this)
  }

  closePopup(event) {
    const { popup } = this.context
    const { onClose } = this.props
    if (popup) {
      popup.hide()
    }
    if (onClose) {
      onClose()
    }
    stopEvent(event)
  }

  render() {
    const { className, style, children } = this.props
    return (
      <div className={classNames('rfd-ui-popup-close-button', className)} style={style} onClick={this.closePopup}>
        {children}
      </div>
    )
  }
}

/*
 * <PopupTrigger>
 */

export class PopupTrigger extends ManagedRenderComponent {
  static propTypes = {
    trigger: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object,
    renderRef: PropTypes.any
  }
  static updateOnProps(/*nextProps, prevProps*/) {
    return true
  }

  static contextTypes = {
    popup: PropTypes.instanceOf(ManagedRenderComponent)
  }

  static childContextTypes = {
    popupTrigger: PropTypes.instanceOf(ManagedRenderComponent).isRequired
  }
  getChildContext() {
    return {
      popupTrigger: this
    }
  }

  constructor(props) {
    super(props)
    this.registerPopup = this.registerPopup.bind(this)
    this.onClickTrigger = this.onClickTrigger.bind(this)
    this.onEnterTrigger = this.onEnterTrigger.bind(this)
    this.onLeaveTrigger = this.onLeaveTrigger.bind(this)
    this.popup = null
  }

  registerPopup(popup) {
    this.popup = popup
  }

  onClickTrigger(event) {
    const popup = this.context.popup || this.popup
    if (popup) popup.onClickTrigger(event, this)
  }

  onEnterTrigger(event) {
    const popup = this.context.popup || this.popup
    if (popup) popup.onEnterTrigger(event, this)
  }

  onLeaveTrigger(event) {
    const popup = this.context.popup || this.popup
    if (popup) popup.onLeaveTrigger(event, this)
  }

  render() {
    const { trigger = DEFAULT_TRIGGER, className, style, children } = this.props
    return (
      <span
        className={classNames('rfd-ui-popup-trigger', className)}
        style={style}
        onClick={'click' == trigger ? this.onClickTrigger : null}
        onMouseEnter={'hover' == trigger ? this.onEnterTrigger : null}
        onMouseLeave={'hover' == trigger ? this.onLeaveTrigger : null}
        ref={this.saveRef('PopupTrigger')}
      >
        {children}
      </span>
    )
  }
}

/*
 * <PopupContentImage>
 */

export class PopupContentImage extends Component {
  static contextTypes = {
    popupContent: PropTypes.instanceOf(PopupContent)
  }

  constructor(...args) {
    super(...args)
    this.onImageLoad = this.onImageLoad.bind(this)
  }

  onImageLoad() {
    const { popupContent } = this.context
    if (popupContent) {
      setTimeout(() => {
        popupContent.reposition()
      }, 0)
      setTimeout(() => {
        // control shot!
        popupContent.reposition()
      }, 100)
    }
  }

  render() {
    return <img alt="" {...this.props} onLoad={this.onImageLoad} onError={this.onImageLoad} />
  }
}
